/**
 * Charge Catalog Service
 *
 * Chargeable items for the Add-charge modal, sourced from ACTIVE menus
 * (Bar & Restaurant recipe items, Merchandising product items) and configured
 * on-request services — not the raw product catalog.
 */
import { apiClient } from '@/shared/services/api';

export type ChargeSourceType =
  | 'menu_recipe_item'
  | 'menu_product_item'
  | 'catalog_item'
  | 'service_product';

export interface ChargeCatalogItem {
  sourceType: ChargeSourceType;
  id: string;
  name: string;
  section: string;
  /** Menu the item belongs to (menu sources only). */
  menu?: string;
  /** Decimal string. */
  price: string;
  tokenEligible: boolean;
  tokenCost: number;
  /** Service sources only. */
  serviceType?: string;
  bookingMode?: string;
}

export interface ChargeCatalog {
  barRestaurant: ChargeCatalogItem[];
  merchandising: ChargeCatalogItem[];
  services: ChargeCatalogItem[];
}

export const getChargeCatalog = async (
  locationId: string,
  forStaff = false,
): Promise<ChargeCatalog> => {
  const response = await apiClient.get<ChargeCatalog>('/api/v1/charge-catalog/', {
    params: { location_id: locationId, ...(forStaff ? { for_staff: 'true' } : {}) },
  });
  return {
    barRestaurant: response.data.barRestaurant ?? [],
    merchandising: response.data.merchandising ?? [],
    services: response.data.services ?? [],
  };
};

/** Map a catalog item to the charge-sellable source id field. */
export function sourceIdPayload(item: ChargeCatalogItem): Record<string, string> {
  switch (item.sourceType) {
    case 'menu_recipe_item':
      return { menuRecipeItemId: item.id };
    case 'menu_product_item':
      return { menuProductItemId: item.id };
    case 'catalog_item':
      return { catalogItemId: item.id };
    case 'service_product':
      return { serviceProductId: item.id };
  }
}
